(ns dashboard.views.analysis.synchronization.events
  (:require [dashboard.entity.analysis.db :as analysis.db]
            [dashboard.entity.questionnaire.db :as-alias questionnaire.db]
            [dashboard.notifications :as notifications]
            [dashboard.services.http-config :as http-config]
            [dashboard.services.questionnaire-list :as questionnaire-list]
            [dashboard.ui :as ui]
            [dashboard.views.analysis.synchronization.config :as config]
            [dashboard.views.analysis.synchronization.view :as-alias view]
            [re-frame.core :as rf]))

(rf/reg-event-db
  ::clear-synchronizing
  (fn [db]
    (assoc-in db [config/app-db-key :synchronizing?] false)))

(defn load-success? [db path]
  (let [{:keys [loading load-success]} (get-in db path)]
    (and (not loading)
         load-success)))

(defn all-synchronized? [db]
  (and (load-success? db [:master-synchronization-state])
       (load-success? db [:questionnaire-list])
       (load-success? db [:structured-analysis-questions])
       (empty (get-in db [config/app-db-key :questionnaires-questions-to-refresh]))))

(defn changes-available? [db]
  (or (seq (get-in db [:master-synchronization-state :data :structured-analyses]))
      (seq (get-in db [:master-synchronization-state :data :removed-template-questionnaires]))
      (seq (get-in db [:master-synchronization-state :data :changed-template-questionnaires]))))

(rf/reg-event-fx
  ::maybe-show-sync-modal
  (fn [{:keys [db]} _]
    (let [sync-reports-done? (get-in db [config/app-db-key :sync-reports-done?])]
      (when (and (all-synchronized? db)
                 (not sync-reports-done?))
        {:db (-> db
                 (assoc-in [config/app-db-key :synchronizing?] false)
                 (assoc-in [config/app-db-key :sync-reports-done?] true))
         :dispatch (if (changes-available? db)
                     [::view/show-modal]
                     [::notifications/add {:message "No changes to synchronize"}])}))))

(rf/reg-event-fx
  ::questionnaire-questions-refreshed
  (fn [{:keys [db]} [_ id]]
    {:db (update-in db [config/app-db-key :questionnaires-questions-to-refresh] disj id)
     :dispatch [::maybe-show-sync-modal]}))

(rf/reg-event-fx
  ::sync-from-master
  (fn [{:keys [db]} [_]]
    (let [questionnaires-to-refresh (set (keep (fn [{:keys [template id]}]
                                                 (when template
                                                   id))
                                               (get-in db [:questionnaire-list :data])))]
      {:db (-> db
               (assoc-in [config/app-db-key :synchronizing?] true)
               (assoc-in [config/app-db-key :sync-reports-done?] false)
               (assoc-in [:master-synchronization-state] {:loading true
                                                          :load-success false})
               (assoc-in [config/app-db-key :questionnaires-questions-to-refresh] questionnaires-to-refresh))
       :http-xhrio {:method :get
                    :uri "/api/structured-analysis/synchronize"
                    :response-format http-config/transit-response-format
                    :on-success [::sync-from-master-success]
                    :on-failure [::sync-from-master-failure]}
       :fx (into [[:dispatch [::analysis.db/get-structured-analysis-questions
                              {:success [::maybe-show-sync-modal]
                               :failure [::clear-synchronizing]}]]
                  [:dispatch [::questionnaire-list/get
                              {:template true}
                              {:success [::maybe-show-sync-modal]
                               :failure [::clear-synchronizing]}]]]
                 ;; Ensure that app-db contains questions for currently stored templates
                 (map (fn [id]
                        [:dispatch [::questionnaire.db/get-questions
                                    id
                                    {:success [::questionnaire.db/get-metadata
                                               id
                                               {:success [::questionnaire-questions-refreshed id]
                                                :failure [::clear-synchronizing]}]
                                     :failure [::clear-synchronizing]}]])
                      questionnaires-to-refresh))})))

;; Note: When updating the list of field that to consider for metadata equality,
;;       remember to also update the view code, that renders the metadata field
;;       and the backend upsert SQL statement
(def questionnaire-metadata-change-field-fns
  [(fn
     ;; Zero arity returns the key of the field
     ;; Arity of one makes the check
     ([] :custom-name)
     ([{:keys [custom-name filename]}]
      (or custom-name filename)))])

(rf/reg-event-fx
  ::sync-from-master-success
  (fn [{:keys [db]} [_ {:keys [structured-analysis-questions] :as data}]]
    (let [my-structured-analysis-questions (apply concat (vals (get-in db [:structured-analysis-questions :data])))
          question-id->master-template-question (into {} (map (juxt :id identity)) (-> data :template-questionnaires :questions))
          changed-template-questionnaires (for [{:keys [id]
                                                 :as template-questionnaire} (sort-by :created
                                                                                      #(compare %2 %1)
                                                                                      (-> data
                                                                                          :template-questionnaires
                                                                                          :questionnaires))
                                                :let [master-mappings (filter #(= id (:question-questionnaire-id %)) structured-analysis-questions)
                                                      master-question->structured-analyses (reduce (fn [acc {:keys [question-id construct-id]}]
                                                                                                     (assert construct-id)
                                                                                                     (update acc question-id (fnil conj #{}) construct-id))
                                                                                                   {}
                                                                                                   master-mappings)

                                                      my-mappings (filter #(= id (:question-questionnaire-id %)) my-structured-analysis-questions)
                                                      my-question-ids (set (map :question-id my-mappings))
                                                      my-question->structured-analyses (reduce (fn [acc {:keys [question-id construct-id]}]
                                                                                                 (assert construct-id)
                                                                                                 (update acc question-id (fnil conj #{}) construct-id))
                                                                                                   {}
                                                                                                   my-mappings)

                                                      mappings-differ? (not= (set (map (juxt :construct-id :question-id) master-mappings))
                                                                             (set (map (juxt :construct-id :question-id) my-mappings)))

                                                      new-mappings (->> master-mappings
                                                                        (remove (fn [{:keys [question-id]}]
                                                                                  (and (contains? my-question-ids question-id)
                                                                                       (= (master-question->structured-analyses question-id)
                                                                                          (my-question->structured-analyses question-id)))))
                                                                        (map (fn [{:keys [question-id] :as mapping}]
                                                                               (assoc mapping :question-ordinal (:ordinal (question-id->master-template-question question-id))))))
                                                      id->question (into {}
                                                                         (map (juxt :id identity))
                                                                         (filter #(= id (:id %))
                                                                                 (get-in db [:questionnaires id :questions :data])))
                                                      removed-mappings (->> (-> data :removed-structured-analysis-questions)
                                                                            (filter #(= id (:question-questionnaire-id %)))
                                                                            (map (fn [{:keys [question-id] :as mapping}]
                                                                                   (assoc mapping :question-ordinal (id->question question-id)))))

                                                      my-template-questionnaire (first (filter #(= id (:id %)) (get-in db [:questionnaire-list :data])))
                                                      questionnaire-metadata-changes (if my-template-questionnaire
                                                                                       (reduce (fn [acc field-fn]
                                                                                                 (let [my (field-fn my-template-questionnaire)
                                                                                                       master (field-fn template-questionnaire)]
                                                                                                   (if (not= my master)
                                                                                                     (assoc acc (field-fn)
                                                                                                            {:my my
                                                                                                             :master master})
                                                                                                     acc)))
                                                                                               {}
                                                                                               questionnaire-metadata-change-field-fns)
                                                                                       {})

                                                      new-template-questionnaire? (not (contains? (set (map :id (get-in db [:questionnaire-list :data]))) id))]
                                                :when (or mappings-differ?
                                                          (seq questionnaire-metadata-changes)
                                                          new-template-questionnaire?)]
                                            {:questionnaire template-questionnaire
                                             :new-mappings new-mappings
                                             :removed-mappings removed-mappings
                                             :metadata-changes questionnaire-metadata-changes})]
      {:db (assoc-in db [:master-synchronization-state] {:data (assoc data :changed-template-questionnaires changed-template-questionnaires)
                                                         :loading false
                                                         :load-success true})
       :dispatch [::maybe-show-sync-modal]})))

(rf/reg-event-fx
  ::sync-from-master-failure
  (fn [{:keys [db]} _]
    {:db (-> db
             (update-in [:master-synchronization-state]
                        assoc
                        :loading false
                        :load-success false)
             (assoc-in [config/app-db-key :synchronizing?] false))
     :fx [[:dispatch [::notifications/add {:message "Failed to synchronize from master"
                                           :class "is-danger"}]]
          [:dispatch [::clear-synchronizing]]]}))

(rf/reg-event-fx
  ::commit-sync
  (fn [{:keys [db]} _]
    (let [{:keys [structured-analyses categories structured-analysis-questions template-questionnaires]} (get-in db [:master-synchronization-state :data])]
      {:db (assoc-in db [config/app-db-key :committing?] true)
       :http-xhrio {:method :post
                    :uri "/api/structured-analysis/synchronize"
                    :params {:structured-analyses structured-analyses
                             :categories categories
                             :structured-analysis-questions structured-analysis-questions
                             :template-questionnaires template-questionnaires}
                    :format http-config/transit-request-format
                    :response-format http-config/transit-response-format
                    :on-success [::commit-sync-success]
                    :on-failure [::commit-sync-failure]}})))

(rf/reg-event-fx
  ::commit-sync-success
  (fn [{:keys [db]} [_]]
    (analysis.db/get-structured-analyses false)
    (analysis.db/get-applied-analyses false)
    (analysis.db/get-structured-analysis-questions false)

    {:db (assoc-in db [config/app-db-key :committing?] false)
     :fx [[:dispatch [::questionnaire-list/get {:template true}]]
          [:dispatch [::ui/pop-modal]]
          [:dispatch [::notifications/add {:message "Synhronization succesful"}]]]}))

(rf/reg-event-fx
  ::commit-sync-failure
  (fn [{:keys [db]} [_]]
    {:db (assoc-in db [config/app-db-key :committing?] false)
     :dispatch [::ui/set-error "Synchronization failed"]}))
